import { useState, useEffect } from "react";
import { useAuth, API, useDateFilter } from "../App";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import DateRangePicker from "../components/DateRangePicker";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Plus, CalendarIcon, Trash2, Package, ShoppingCart, TrendingUp, Store, Truck, Pencil, Cloud, ShoppingBag } from "lucide-react";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

const CHART_COLORS = ["#F43F5E", "#10B981", "#F59E0B", "#3B82F6", "#8B5CF6", "#EC4899", "#f59e0b", "#ec4899"];

const SalesPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const { startDate, endDate } = useDateFilter();
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Filters
  const [sourceFilter, setSourceFilter] = useState("all"); // "all", "odoo", "manual"
  const [categoryFilter, setCategoryFilter] = useState("all"); // "all" or product_id
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(30);
  
  // Dialogs
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [saleDialogOpen, setSaleDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingSale, setEditingSale] = useState(null);
  
  // Product form
  const [productForm, setProductForm] = useState({
    name: "",
    cost_percentage: "",
    heladeria_margin_percentage: "",
    merch_margin_percentage: "0",
    is_merch_product: false
  });
  
  // Sale form
  const [saleForm, setSaleForm] = useState({
    date: new Date(),
    product_id: "",
    gross_total: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchSalesData();
  }, [startDate, endDate]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/products`, getAuthHeaders());
      setProducts(response.data);
    } catch (error) {
      toast.error("Error al cargar productos");
    }
  };

  const fetchSalesData = async () => {
    try {
      setLoading(true);
      const startStr = format(startDate, 'yyyy-MM-dd');
      const endStr = format(endDate, 'yyyy-MM-dd');
      const [salesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/sales?start_date=${startStr}&end_date=${endStr}`, getAuthHeaders()),
        axios.get(`${API}/sales/summary?start_date=${startStr}&end_date=${endStr}`, getAuthHeaders())
      ]);
      setSales(salesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error("Error al cargar ventas");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: productForm.name,
        cost_percentage: parseFloat(productForm.cost_percentage),
        heladeria_margin_percentage: parseFloat(productForm.heladeria_margin_percentage),
        merch_margin_percentage: parseFloat(productForm.merch_margin_percentage || 0),
        is_merch_product: productForm.is_merch_product
      };
      
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, data, getAuthHeaders());
        toast.success("Producto actualizado");
      } else {
        await axios.post(`${API}/products`, data, getAuthHeaders());
        toast.success("Producto creado");
      }
      
      setProductDialogOpen(false);
      resetProductForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar producto");
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      cost_percentage: product.cost_percentage.toString(),
      heladeria_margin_percentage: product.heladeria_margin_percentage.toString(),
      merch_margin_percentage: product.merch_margin_percentage.toString(),
      is_merch_product: product.is_merch_product || false
    });
    setProductDialogOpen(true);
  };

  const handleDeleteProduct = async (id) => {
    if (!confirm("¿Estás seguro de eliminar este producto?")) return;
    
    try {
      await axios.delete(`${API}/products/${id}`, getAuthHeaders());
      toast.success("Producto eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar producto");
    }
  };

  const handleCreateSale = async (e) => {
    e.preventDefault();
    try {
      const data = {
        date: format(saleForm.date, "yyyy-MM-dd"),
        product_id: saleForm.product_id,
        gross_total: parseFloat(saleForm.gross_total),
        notes: saleForm.notes
      };
      
      if (editingSale) {
        await axios.put(`${API}/sales/${editingSale.id}`, data, getAuthHeaders());
        toast.success("Venta actualizada");
      } else {
        await axios.post(`${API}/sales`, data, getAuthHeaders());
        toast.success("Venta registrada");
      }
      
      setSaleDialogOpen(false);
      resetSaleForm();
      fetchSalesData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar venta");
    }
  };

  const handleEditSale = (sale) => {
    setEditingSale(sale);
    setSaleForm({
      date: parseISO(sale.date),
      product_id: sale.product_id,
      gross_total: sale.gross_total.toString(),
      notes: sale.notes || ""
    });
    setSaleDialogOpen(true);
  };

  // Bulk selection handlers - only allow selecting manual sales
  const handleToggleSelect = (id) => {
    // Find the sale and check if it's from Odoo
    const sale = paginatedSales.find(s => s.id === id);
    if (sale?.source === 'odoo') {
      toast.error("Las ventas de Odoo no pueden eliminarse");
      return;
    }
    
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleSelectAll = () => {
    // Only select manual sales, not Odoo sales
    const manualSales = paginatedSales.filter(s => s.source !== 'odoo');
    if (manualSales.length === 0) {
      toast.error("No hay ventas manuales para seleccionar");
      return;
    }
    
    const manualIds = manualSales.map(s => s.id);
    const allSelected = manualIds.every(id => selectedIds.includes(id));
    
    if (allSelected) {
      setSelectedIds(selectedIds.filter(id => !manualIds.includes(id)));
    } else {
      setSelectedIds([...new Set([...selectedIds, ...manualIds])]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    // Double check no Odoo sales are selected
    const hasOdooSales = selectedIds.some(id => {
      const sale = sales.find(s => s.id === id);
      return sale?.source === 'odoo';
    });
    
    if (hasOdooSales) {
      toast.error("Las ventas de Odoo no pueden eliminarse");
      return;
    }
    
    if (!confirm(`¿Estás seguro de eliminar ${selectedIds.length} ventas manuales?`)) return;
    
    try {
      await axios.post(`${API}/sales/bulk-delete`, selectedIds, getAuthHeaders());
      toast.success(`${selectedIds.length} ventas eliminadas`);
      setSelectedIds([]);
      fetchSalesData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar ventas");
    }
  };

  const handleDeleteSale = async (saleId) => {
    const sale = sales.find(s => s.id === saleId);
    if (sale?.source === 'odoo') {
      toast.error("Las ventas de Odoo no pueden eliminarse. Deben cancelarse en Odoo.");
      return;
    }
    
    if (!confirm("¿Eliminar esta venta?")) return;
    
    try {
      await axios.delete(`${API}/sales/${saleId}`, getAuthHeaders());
      toast.success("Venta eliminada");
      fetchSalesData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    }
  };

  const resetProductForm = () => {
    setEditingProduct(null);
    setProductForm({
      name: "",
      cost_percentage: "",
      heladeria_margin_percentage: "",
      merch_margin_percentage: "0",
      is_merch_product: false
    });
  };

  const resetSaleForm = () => {
    setEditingSale(null);
    setSaleForm({
      date: new Date(),
      product_id: "",
      gross_total: "",
      notes: ""
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(value || 0);
  };

  // Filtered sales based on source and category filter
  const filteredSales = sales.filter(s => {
    // Source filter
    if (sourceFilter !== "all" && s.source !== sourceFilter) return false;
    // Category filter
    if (categoryFilter !== "all" && s.product_id !== categoryFilter) return false;
    return true;
  });

  // Get unique categories from sales for filter dropdown
  const salesCategories = [...new Map(
    sales.map(s => [s.product_id, { id: s.product_id, name: s.product_name }])
  ).values()].sort((a, b) => a.name.localeCompare(b.name));

  // Pagination
  const totalPages = Math.ceil(filteredSales.length / itemsPerPage);
  const paginatedSales = filteredSales.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to page 1 when filter or itemsPerPage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sourceFilter, categoryFilter, itemsPerPage, startDate, endDate]);

  // Calculate preview for sale form
  const selectedProduct = products.find(p => p.id === saleForm.product_id);
  const grossAmount = parseFloat(saleForm.gross_total) || 0;
  const previewCost = selectedProduct ? grossAmount * (selectedProduct.cost_percentage / 100) : 0;
  const previewHeladeria = selectedProduct ? grossAmount * (selectedProduct.heladeria_margin_percentage / 100) : 0;
  const previewMerch = selectedProduct ? grossAmount * (selectedProduct.merch_margin_percentage / 100) : 0;

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-[#FECDD3] rounded-lg p-3 shadow-xl">
          <p className="text-[#4A4A4A] font-medium">{payload[0].name || payload[0].payload.name}</p>
          <p className="text-[#F43F5E] font-mono">{formatCurrency(payload[0].value)}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="sales-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#4A4A4A] font-['Manrope']">Ventas</h1>
            <p className="text-[#6B7280]">Registra ventas y gestiona productos</p>
          </div>
          
          <DateRangePicker />
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-white border-[#FECDD3] card-hover" data-testid="total-gross-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#6B7280] text-sm">Venta Bruta Total</p>
                  <p className="text-2xl font-bold text-[#4A4A4A] font-mono mt-1">
                    {formatCurrency(summary?.total_gross)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-[#F43F5E]/10 flex items-center justify-center">
                  <ShoppingCart className="w-6 h-6 text-[#F43F5E]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#FECDD3] card-hover" data-testid="total-cost-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#6B7280] text-sm">Costo Total</p>
                  <p className="text-2xl font-bold text-[#F43F5E] font-mono mt-1">
                    {formatCurrency(summary?.total_cost)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-[#F43F5E]/10 flex items-center justify-center">
                  <Package className="w-6 h-6 text-[#F43F5E]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#FECDD3] card-hover" data-testid="store-margin-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#6B7280] text-sm">Margen Heladería 🍦</p>
                  <p className="text-2xl font-bold text-[#10B981] font-mono mt-1">
                    {formatCurrency(summary?.total_heladeria_margin)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
                  <Store className="w-6 h-6 text-[#10B981]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#FECDD3] card-hover" data-testid="supplier-margin-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#6B7280] text-sm">Margen Merch 👕</p>
                  <p className="text-2xl font-bold text-[#F59E0B] font-mono mt-1">
                    {formatCurrency(summary?.total_merch_margin)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-[#FEF3C7] flex items-center justify-center">
                  <Truck className="w-6 h-6 text-[#F59E0B]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="register" className="space-y-6">
          <TabsList className="bg-[#FEE2E2] border-[#FECDD3] flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="register" className="data-[state=active]:bg-[#F43F5E] data-[state=active]:text-white" data-testid="register-tab">
              <ShoppingCart className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Registrar</span> Ventas
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-[#F43F5E] data-[state=active]:text-white" data-testid="analytics-tab">
              <TrendingUp className="w-4 h-4 mr-2" />
              Análisis
            </TabsTrigger>
          </TabsList>

          {/* Register Sales Tab */}
          <TabsContent value="register" className="space-y-6">
            <div className="flex justify-end">
              <Dialog open={saleDialogOpen} onOpenChange={(open) => { setSaleDialogOpen(open); if (!open) resetSaleForm(); }}>
                <DialogTrigger asChild>
                  <Button className="bg-[#F43F5E] hover:bg-[#F43F5E]/80 text-white btn-glow" data-testid="new-sale-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Registrar Venta
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white border-[#FECDD3] text-[#4A4A4A] max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-['Manrope']">{editingSale ? "Editar Venta" : "Registrar Venta"}</DialogTitle>
                  </DialogHeader>
                  <p className="text-[#6B7280] text-sm -mt-2">Selecciona un producto y el monto bruto vendido</p>
                  <form onSubmit={handleCreateSale} className="space-y-4">
                    {/* Date */}
                    <div className="space-y-2">
                      <Label className="text-[#6B7280]">Fecha</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button 
                            variant="outline" 
                            className="w-full justify-start text-left bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] hover:bg-[#FEE2E2]"
                            data-testid="sale-date-picker"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {format(saleForm.date, "PPP", { locale: es })}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-[#FEE2E2] border-[#FECDD3]" align="start">
                          <Calendar
                            mode="single"
                            selected={saleForm.date}
                            onSelect={(date) => date && setSaleForm({...saleForm, date})}
                            locale={es}
                            className="rounded-md"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Product */}
                    <div className="space-y-2">
                      <Label className="text-[#6B7280]">Producto</Label>
                      <Select value={saleForm.product_id} onValueChange={(v) => setSaleForm({...saleForm, product_id: v})}>
                        <SelectTrigger className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A]" data-testid="sale-product-select">
                          <SelectValue placeholder="Seleccionar producto" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#FEE2E2] border-[#FECDD3]">
                          {products.filter(p => p.is_active).map((p) => (
                            <SelectItem key={p.id} value={p.id} className="text-[#4A4A4A] hover:bg-[#FEE2E2]">
                              <div className="flex items-center gap-2">
                                {p.source === 'odoo' && <Cloud className="w-3 h-3 text-[#8B5CF6]" />}
                                {p.source === 'shopify' && <ShoppingBag className="w-3 h-3 text-[#95BF47]" />}
                                {(!p.source || p.source === 'manual') && <Package className="w-3 h-3 text-gray-400" />}
                                <span>{p.name} ({p.heladeria_margin_percentage}% heladería)</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Gross Total */}
                    <div className="space-y-2">
                      <Label className="text-[#6B7280]">Venta Bruta Total (MXN)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={saleForm.gross_total}
                        onChange={(e) => setSaleForm({...saleForm, gross_total: e.target.value})}
                        placeholder="0.00"
                        required
                        data-testid="sale-gross-input"
                        className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] placeholder:text-[#6B7280]/50 font-mono"
                      />
                    </div>

                    {/* Preview Calculation */}
                    {selectedProduct && grossAmount > 0 && (
                      <div className="bg-[#FEE2E2]/50 rounded-lg p-4 space-y-2 border border-[#FECDD3]">
                        <p className="text-[#6B7280] text-sm font-medium">Desglose calculado:</p>
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div>
                            <p className="text-[#6B7280]/70">Costo ({selectedProduct.cost_percentage}%)</p>
                            <p className="text-[#F43F5E] font-mono">{formatCurrency(previewCost)}</p>
                          </div>
                          <div>
                            <p className="text-[#6B7280]/70">Heladería ({selectedProduct.heladeria_margin_percentage}%)</p>
                            <p className="text-[#10B981] font-mono">{formatCurrency(previewHeladeria)}</p>
                          </div>
                          <div>
                            <p className="text-[#6B7280]/70">Merch ({selectedProduct.merch_margin_percentage}%)</p>
                            <p className="text-[#F59E0B] font-mono">{formatCurrency(previewMerch)}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    <div className="space-y-2">
                      <Label className="text-[#6B7280]">Notas (opcional)</Label>
                      <Textarea
                        value={saleForm.notes}
                        onChange={(e) => setSaleForm({...saleForm, notes: e.target.value})}
                        placeholder="Notas adicionales..."
                        data-testid="sale-notes-input"
                        className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] placeholder:text-[#6B7280]/50 min-h-[60px]"
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setSaleDialogOpen(false)} className="flex-1 bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] hover:bg-[#FEE2E2]">
                        Cancelar
                      </Button>
                      <Button type="submit" className="flex-1 bg-[#F43F5E] hover:bg-[#F43F5E]/80 text-white" data-testid="save-sale-button">
                        {editingSale ? "Actualizar" : "Registrar"}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Bulk Selection Bar */}
            {selectedIds.length > 0 && (
              <div className="flex items-center justify-between bg-[#F43F5E]/10 border border-[#F43F5E]/30 rounded-lg px-4 py-3 mb-4">
                <span className="text-[#F43F5E] font-medium">
                  {selectedIds.length} venta{selectedIds.length > 1 ? 's' : ''} seleccionada{selectedIds.length > 1 ? 's' : ''}
                </span>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                  className="bg-[#F43F5E] hover:bg-[#E11D48]"
                  data-testid="bulk-delete-sales-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar Seleccionadas
                </Button>
              </div>
            )}

            {/* Sales Table */}
            <Card className="bg-white border-[#FECDD3]">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <CardTitle className="text-[#4A4A4A] font-['Manrope']">Ventas del Mes</CardTitle>
                    <CardDescription className="text-[#6B7280]">
                      {filteredSales.length} registros 
                      {(sourceFilter !== "all" || categoryFilter !== "all") && " (filtrado"}
                      {sourceFilter !== "all" && ` por ${sourceFilter === "odoo" ? "Odoo" : "Manual"}`}
                      {categoryFilter !== "all" && ` ${sourceFilter !== "all" ? "y" : "por"} ${salesCategories.find(c => c.id === categoryFilter)?.name || "categoría"}`}
                      {(sourceFilter !== "all" || categoryFilter !== "all") && ")"}
                    </CardDescription>
                  </div>
                  
                  {/* Filters and Pagination Controls */}
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Category Filter */}
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                      <SelectTrigger className="w-40 bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] h-9" data-testid="category-filter">
                        <SelectValue placeholder="Categoría" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-[#FECDD3] max-h-[300px]">
                        <SelectItem value="all" className="text-[#4A4A4A]">Todas las categorías</SelectItem>
                        {salesCategories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id} className="text-[#4A4A4A]">
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* Source Filter */}
                    <Select value={sourceFilter} onValueChange={setSourceFilter}>
                      <SelectTrigger className="w-32 bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] h-9" data-testid="source-filter">
                        <SelectValue placeholder="Origen" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-[#FECDD3]">
                        <SelectItem value="all" className="text-[#4A4A4A]">Todos</SelectItem>
                        <SelectItem value="odoo" className="text-[#4A4A4A]">
                          <div className="flex items-center gap-2">
                            <Cloud className="w-3 h-3 text-[#8B5CF6]" />
                            Odoo
                          </div>
                        </SelectItem>
                        <SelectItem value="manual" className="text-[#4A4A4A]">Manual</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Items per page */}
                    <Select value={itemsPerPage.toString()} onValueChange={(v) => setItemsPerPage(parseInt(v))}>
                      <SelectTrigger className="w-24 bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] h-9" data-testid="items-per-page">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-[#FECDD3]">
                        <SelectItem value="10" className="text-[#4A4A4A]">10</SelectItem>
                        <SelectItem value="30" className="text-[#4A4A4A]">30</SelectItem>
                        <SelectItem value="50" className="text-[#4A4A4A]">50</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center h-48">
                    <div className="spinner w-8 h-8"></div>
                  </div>
                ) : filteredSales.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-48 text-[#6B7280]">
                    <ShoppingCart className="w-12 h-12 mb-2 opacity-50" />
                    <p>No hay ventas {sourceFilter !== "all" ? `de ${sourceFilter === "odoo" ? "Odoo" : "entrada manual"}` : ""} este mes</p>
                  </div>
                ) : (
                  <>
                    {/* Mobile Cards */}
                    <div className="lg:hidden p-4 space-y-3">
                      {/* Select All - Mobile */}
                      <div className="flex items-center gap-3 pb-3 border-b border-[#FEE2E2]">
                        <Checkbox
                          checked={selectedIds.length === paginatedSales.length && paginatedSales.length > 0}
                          onCheckedChange={() => {
                            if (selectedIds.length === paginatedSales.length) {
                              setSelectedIds([]);
                            } else {
                              // Only select manual sales
                              setSelectedIds(paginatedSales.filter(s => s.source !== 'odoo').map(s => s.id));
                            }
                          }}
                          data-testid="select-all-sales-mobile"
                        />
                        <span className="text-sm text-[#6B7280]">Seleccionar manuales ({paginatedSales.filter(s => s.source !== 'odoo').length})</span>
                      </div>
                      
                      {paginatedSales.map((s) => (
                        <div 
                          key={s.id}
                          data-testid={`sale-card-${s.id}`}
                          className={`p-4 rounded-lg border ${selectedIds.includes(s.id) ? 'border-[#F43F5E] bg-[#F43F5E]/5' : 'border-[#FEE2E2] bg-[#FFFAFA]'}`}
                        >
                          {/* Header: Checkbox, Date and Product */}
                          <div className="flex items-start gap-3 mb-3">
                            <Checkbox
                              checked={selectedIds.includes(s.id)}
                              onCheckedChange={() => handleToggleSelect(s.id)}
                              disabled={s.source === 'odoo'}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="flex items-start justify-between">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="text-[#4A4A4A] font-medium">{s.product_name}</p>
                                    {s.source === 'odoo' && (
                                      <Badge variant="outline" className="text-xs bg-[#8B5CF6]/10 text-[#8B5CF6] border-[#8B5CF6]/30">
                                        <Cloud className="w-3 h-3 mr-1" />
                                        Odoo
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-[#6B7280] font-mono">{format(parseISO(s.date), "dd/MM/yyyy")}</p>
                                  {s.odoo_reference && (
                                    <p className="text-xs text-[#9CA3AF] font-mono">{s.odoo_reference}</p>
                                  )}
                                </div>
                                <span className="text-lg font-bold text-[#3B82F6] font-mono">
                                  {formatCurrency(s.gross_total)}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Breakdown */}
                          <div className="grid grid-cols-3 gap-2 mb-3 p-3 bg-[#FEE2E2]/30 rounded-lg">
                            <div className="text-center">
                              <p className="text-xs text-[#6B7280]">Costo</p>
                              <p className="text-sm font-mono text-[#EF4444]">{formatCurrency(s.cost_amount)}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-[#6B7280]">Heladería 🍦</p>
                              <p className="text-sm font-mono text-[#10B981]">{formatCurrency(s.heladeria_margin_amount)}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-xs text-[#6B7280]">Merch 👕</p>
                              <p className="text-sm font-mono text-[#F59E0B]">{formatCurrency(s.merch_margin_amount)}</p>
                            </div>
                          </div>
                          
                          {/* Footer: User and Actions */}
                          <div className="flex items-center justify-between pt-3 border-t border-[#FEE2E2]">
                            <span className="text-xs text-[#9CA3AF]">Por: {s.user_name}</span>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditSale(s)}
                                className="h-8 w-8 p-0 text-[#6B7280] hover:text-[#4A4A4A] hover:bg-[#FEE2E2]"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              {s.source !== 'odoo' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteSale(s.id)}
                                  className="h-8 w-8 p-0 text-[#6B7280] hover:text-[#F43F5E] hover:bg-[#F43F5E]/10"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Desktop Table */}
                    <div className="hidden lg:block overflow-x-auto">
                      <table className="data-table" data-testid="sales-table">
                        <thead>
                          <tr className="bg-[#FEE2E2]/50">
                            <th className="w-10">
                              <Checkbox
                                checked={paginatedSales.filter(s => s.source !== 'odoo').length > 0 && 
                                         paginatedSales.filter(s => s.source !== 'odoo').every(s => selectedIds.includes(s.id))}
                                onCheckedChange={handleSelectAll}
                                data-testid="select-all-sales-checkbox"
                              />
                            </th>
                            <th>Fecha</th>
                            <th>Producto</th>
                            <th className="text-[#3B82F6]">Venta Bruta</th>
                            <th className="text-[#EF4444]">Costo</th>
                            <th className="text-[#10B981]">Heladería 🍦</th>
                            <th className="text-[#F59E0B]">Merch 👕</th>
                            <th>Origen</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedSales.map((s) => (
                            <tr key={s.id} data-testid={`sale-row-${s.id}`} className={selectedIds.includes(s.id) ? 'bg-[#F43F5E]/5' : ''}>
                              <td>
                                <Checkbox
                                  checked={selectedIds.includes(s.id)}
                                  onCheckedChange={() => handleToggleSelect(s.id)}
                                  disabled={s.source === 'odoo'}
                                />
                              </td>
                              <td className="font-mono text-sm">{format(parseISO(s.date), "dd/MM/yyyy")}</td>
                              <td className="text-[#4A4A4A] font-medium">
                                <div>
                                  {s.product_name}
                                  {s.odoo_reference && (
                                    <p className="text-xs text-[#9CA3AF] font-mono">{s.odoo_reference}</p>
                                  )}
                                </div>
                              </td>
                              <td className="font-mono font-semibold" style={{color: '#3B82F6'}}>{formatCurrency(s.gross_total)}</td>
                              <td className="font-mono" style={{color: '#EF4444'}}>{formatCurrency(s.cost_amount)}</td>
                              <td className="font-mono" style={{color: '#10B981'}}>{formatCurrency(s.heladeria_margin_amount)}</td>
                              <td className="font-mono" style={{color: '#F59E0B'}}>{formatCurrency(s.merch_margin_amount)}</td>
                              <td>
                                {s.source === 'odoo' ? (
                                  <Badge variant="outline" className="text-xs bg-[#8B5CF6]/10 text-[#8B5CF6] border-[#8B5CF6]/30">
                                    <Cloud className="w-3 h-3 mr-1" />
                                    Odoo
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs bg-gray-100 text-gray-600 border-gray-200">
                                    Manual
                                  </Badge>
                                )}
                              </td>
                              <td>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditSale(s)}
                                    data-testid={`edit-sale-${s.id}`}
                                    className="text-[#6B7280] hover:text-[#4A4A4A] hover:bg-[#FEE2E2]"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  {s.source !== 'odoo' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteSale(s.id)}
                                      data-testid={`delete-sale-${s.id}`}
                                      className="text-[#6B7280] hover:text-[#F43F5E] hover:bg-[#F43F5E]/10"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-[#FEE2E2]">
                        <p className="text-sm text-[#6B7280]">
                          Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredSales.length)} de {filteredSales.length}
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(1)}
                            disabled={currentPage === 1}
                            className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] hover:bg-[#FECDD3] disabled:opacity-50"
                          >
                            Primera
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] hover:bg-[#FECDD3] disabled:opacity-50"
                          >
                            Anterior
                          </Button>
                          <span className="px-3 py-1 text-sm text-[#4A4A4A] bg-[#F43F5E]/10 rounded">
                            {currentPage} / {totalPages}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] hover:bg-[#FECDD3] disabled:opacity-50"
                          >
                            Siguiente
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages}
                            className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] hover:bg-[#FECDD3] disabled:opacity-50"
                          >
                            Última
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Products Catalog Tab */}
          <TabsContent value="products" className="space-y-6">
            <div className="flex justify-end">
              <Dialog open={productDialogOpen} onOpenChange={(open) => { setProductDialogOpen(open); if (!open) resetProductForm(); }}>
                <DialogTrigger asChild>
                  <Button className="bg-[#F43F5E] hover:bg-[#F43F5E]/80 text-white btn-glow" data-testid="new-product-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Producto
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white border-[#FECDD3] text-[#4A4A4A] max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-['Manrope']">
                      {editingProduct ? "Editar Producto" : "Nuevo Producto"}
                    </DialogTitle>
                  </DialogHeader>
                  <p className="text-[#6B7280] text-sm -mt-2">Define los porcentajes de distribución (deben sumar 100%)</p>
                  <form onSubmit={handleCreateProduct} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-[#6B7280]">Nombre del Producto</Label>
                      <Input
                        value={productForm.name}
                        onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                        placeholder="Ej: Playeras"
                        required
                        data-testid="product-name-input"
                        className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] placeholder:text-[#6B7280]/50"
                      />
                    </div>

                    <div className="bg-[#FEE2E2]/50 rounded-lg p-4 space-y-4 border border-[#FECDD3]">
                      <p className="text-[#6B7280] text-sm font-medium">Distribución de porcentajes (deben sumar 100%)</p>
                      
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className="text-[#6B7280]/70 text-xs">% Costo</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={productForm.cost_percentage}
                            onChange={(e) => setProductForm({...productForm, cost_percentage: e.target.value})}
                            placeholder="42"
                            required
                            data-testid="product-cost-input"
                            className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] placeholder:text-[#6B7280]/50 font-mono"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[#6B7280]/70 text-xs">% Heladería 🍦</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={productForm.heladeria_margin_percentage}
                            onChange={(e) => setProductForm({...productForm, heladeria_margin_percentage: e.target.value})}
                            placeholder="58"
                            required
                            data-testid="product-heladeria-input"
                            className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] placeholder:text-[#6B7280]/50 font-mono"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[#6B7280]/70 text-xs">% Merch 👕</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={productForm.merch_margin_percentage}
                            onChange={(e) => setProductForm({...productForm, merch_margin_percentage: e.target.value})}
                            placeholder="0"
                            required
                            data-testid="product-merch-input"
                            className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] placeholder:text-[#6B7280]/50 font-mono"
                          />
                        </div>
                      </div>

                      {/* Sum indicator */}
                      {(productForm.cost_percentage || productForm.heladeria_margin_percentage || productForm.merch_margin_percentage) && (
                        <div className="text-center">
                          <span className={`text-sm font-mono ${
                            Math.abs((parseFloat(productForm.cost_percentage || 0) + parseFloat(productForm.heladeria_margin_percentage || 0) + parseFloat(productForm.merch_margin_percentage || 0)) - 100) < 0.01
                              ? 'text-[#10B981]'
                              : 'text-[#F43F5E]'
                          }`}>
                            Total: {(parseFloat(productForm.cost_percentage || 0) + parseFloat(productForm.heladeria_margin_percentage || 0) + parseFloat(productForm.merch_margin_percentage || 0)).toFixed(2)}%
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Checkbox para producto de Merch */}
                    <div className="flex items-center space-x-3 bg-[#FEF3C7]/50 border border-[#F59E0B]/30 rounded-lg p-4">
                      <Checkbox
                        id="is_merch_product"
                        checked={productForm.is_merch_product}
                        onCheckedChange={(checked) => setProductForm({...productForm, is_merch_product: checked})}
                        className="border-[#F59E0B] data-[state=checked]:bg-[#F59E0B] data-[state=checked]:border-[#F59E0B]"
                        data-testid="is-merch-product-checkbox"
                      />
                      <div className="flex-1">
                        <Label htmlFor="is_merch_product" className="text-[#B45309] font-medium cursor-pointer">
                          Producto de Merch 👕
                        </Label>
                        <p className="text-[#D97706] text-xs mt-0.5">
                          Marcar si el costo del producto debe contabilizarse como inversión de la Merch
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setProductDialogOpen(false)} className="flex-1 bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] hover:bg-[#FEE2E2]">
                        Cancelar
                      </Button>
                      <Button type="submit" className="flex-1 bg-[#F43F5E] hover:bg-[#F43F5E]/80 text-white" data-testid="save-product-button">
                        {editingProduct ? "Actualizar" : "Guardar"}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((product) => (
                <Card key={product.id} className={`bg-white border-[#FECDD3] card-hover ${product.is_merch_product ? 'ring-2 ring-[#F59E0B]/30' : ''}`} data-testid={`product-card-${product.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          product.source === 'shopify' ? 'bg-[#95BF47]/20' :
                          product.source === 'odoo' ? 'bg-[#8B5CF6]/20' :
                          product.is_merch_product ? 'bg-[#F59E0B]/20' : 'bg-[#F43F5E]/20'
                        }`}>
                          {product.source === 'shopify' ? (
                            <ShoppingBag className="w-5 h-5 text-[#95BF47]" />
                          ) : product.source === 'odoo' ? (
                            <Cloud className="w-5 h-5 text-[#8B5CF6]" />
                          ) : (
                            <Package className={`w-5 h-5 ${product.is_merch_product ? 'text-[#F59E0B]' : 'text-[#F43F5E]'}`} />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-[#4A4A4A] font-medium">{product.name}</h3>
                            {/* Source Badge */}
                            {product.source === 'odoo' && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-[#8B5CF6]/10 text-[#8B5CF6] border-[#8B5CF6]/30" data-testid={`product-source-badge-${product.id}`}>
                                <Cloud className="w-3 h-3 mr-1" />
                                Odoo
                              </Badge>
                            )}
                            {product.source === 'shopify' && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-[#95BF47]/10 text-[#95BF47] border-[#95BF47]/30" data-testid={`product-source-badge-${product.id}`}>
                                <ShoppingBag className="w-3 h-3 mr-1" />
                                Shopify
                              </Badge>
                            )}
                            {(!product.source || product.source === 'manual') && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-gray-100 text-gray-600 border-gray-200" data-testid={`product-source-badge-${product.id}`}>
                                Manual
                              </Badge>
                            )}
                            {/* Merch Badge */}
                            {product.is_merch_product && (
                              <Badge className="bg-[#F59E0B]/20 text-[#B45309] text-[10px] px-1.5 py-0 hover:bg-[#F59E0B]/20">
                                Merch
                              </Badge>
                            )}
                          </div>
                          <span className={`text-xs ${product.is_active ? 'text-[#10B981]' : 'text-[#6B7280]'}`}>
                            {product.is_active ? 'Activo' : 'Inactivo'}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditProduct(product)}
                          data-testid={`edit-product-${product.id}`}
                          className="text-[#6B7280] hover:text-[#4A4A4A] hover:bg-[#FEE2E2]"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteProduct(product.id)}
                          data-testid={`delete-product-${product.id}`}
                          className="text-[#6B7280] hover:text-[#F43F5E] hover:bg-[#F43F5E]/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-[#6B7280]">Costo</span>
                        <span className="text-[#F43F5E] font-mono">{product.cost_percentage}%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#6B7280]">Heladería 🍦</span>
                        <span className="text-[#10B981] font-mono">{product.heladeria_margin_percentage}%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[#6B7280]">Merch 👕</span>
                        <span className="text-[#F59E0B] font-mono">{product.merch_margin_percentage}%</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Sales by Product */}
              <Card className="bg-white border-[#FECDD3]" data-testid="sales-by-product-chart">
                <CardHeader>
                  <CardTitle className="text-[#4A4A4A] font-['Manrope']">Ventas por Producto</CardTitle>
                </CardHeader>
                <CardContent>
                  {summary?.sales_by_product?.length > 0 ? (
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={summary.sales_by_product}
                            dataKey="gross_total"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                            labelLine={{ stroke: '#4d4343' }}
                          >
                            {summary.sales_by_product.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-72 flex items-center justify-center text-[#6B7280]">
                      No hay datos de ventas para este mes
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Margins by Product */}
              <Card className="bg-white border-[#FECDD3]" data-testid="margins-by-product-chart">
                <CardHeader>
                  <CardTitle className="text-[#4A4A4A] font-['Manrope']">Márgenes por Producto</CardTitle>
                </CardHeader>
                <CardContent>
                  {summary?.sales_by_product?.length > 0 ? (
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={summary.sales_by_product} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#FEE2E2" />
                          <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} stroke="#6B7280" />
                          <YAxis type="category" dataKey="name" stroke="#6B7280" width={100} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <Bar dataKey="heladeria_margin_amount" name="Heladería 🍦" fill="#10B981" stackId="a" />
                          <Bar dataKey="merch_margin_amount" name="Merch 👕" fill="#F59E0B" stackId="a" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-72 flex items-center justify-center text-[#6B7280]">
                      No hay datos de ventas para este mes
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Monthly Trend */}
            <Card className="bg-white border-[#FECDD3]" data-testid="sales-monthly-trend">
              <CardHeader>
                <CardTitle className="text-[#4A4A4A] font-['Manrope']">Tendencia de Ventas (Últimos 6 meses)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={summary?.monthly_trend || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#FEE2E2" />
                      <XAxis dataKey="month" stroke="#6B7280" />
                      <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} stroke="#6B7280" />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="gross" name="Venta Bruta" fill="#F43F5E" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="heladeria_margin" name="Heladería 🍦" fill="#10B981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="merch_margin" name="Merch 👕" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Product Details Table */}
            {summary?.sales_by_product?.length > 0 && (
              <Card className="bg-white border-[#FECDD3]" data-testid="sales-details-table">
                <CardHeader>
                  <CardTitle className="text-[#4A4A4A] font-['Manrope']">Detalle por Producto</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr className="bg-[#FEE2E2]/50">
                          <th>Producto</th>
                          <th>Venta Bruta</th>
                          <th>Costo</th>
                          <th>Heladería 🍦</th>
                          <th>Merch 👕</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.sales_by_product.map((p, i) => (
                          <tr key={i}>
                            <td className="text-[#4A4A4A] font-medium">{p.name}</td>
                            <td className="font-mono text-[#4A4A4A]">{formatCurrency(p.gross_total)}</td>
                            <td className="font-mono text-[#F43F5E]">{formatCurrency(p.cost_amount)}</td>
                            <td className="font-mono text-[#10B981]">{formatCurrency(p.heladeria_margin_amount)}</td>
                            <td className="font-mono text-[#F59E0B]">{formatCurrency(p.merch_margin_amount)}</td>
                          </tr>
                        ))}
                        <tr className="bg-[#FEE2E2]/30 font-bold">
                          <td className="text-[#4A4A4A]">TOTAL</td>
                          <td className="font-mono text-[#4A4A4A]">{formatCurrency(summary.total_gross)}</td>
                          <td className="font-mono text-[#F43F5E]">{formatCurrency(summary.total_cost)}</td>
                          <td className="font-mono text-[#10B981]">{formatCurrency(summary.total_heladeria_margin)}</td>
                          <td className="font-mono text-[#F59E0B]">{formatCurrency(summary.total_merch_margin)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default SalesPage;
